import type { MenuCopy, SiteAuthCopy } from '@/lib/app-shell/copy'
import { menuRoutes, type MenuMode } from '@/lib/app-shell/menu-routes'
import type { CityCopy } from '@/lib/content-standard/entity-copy'
import type { MenuItem, NavbarAuth, NavbarLogo } from '@/components/navbar'
import { defaultLocale, type Locale, type PlatformLocale } from '@/i18n/locales'

/**
 * The site's shared navigation, built per locale for the global shell (`AppLayout`).
 *
 * Three kinds of content meet here and are deliberately kept apart:
 *
 *   - **Copy** — section titles, the route anchors' templates, account labels — lives in
 *     the translation store (`src/content/translations/app-shell/navigation.json`), so
 *     translators own every visible string, and arrives rendered by the navigation copy set.
 *   - **City names** are the `city` entity copy, read by id in the reader's locale, so the
 *     menu spells a city the way every other surface does.
 *   - **Structure** — which routes each dropdown promotes, and the paths they point at
 *     — is editorial routing data: the routes by market (`menu-routes.ts`), and the
 *     carriers here by locale, since the promoted carriers differ per locale (mirrors
 *     ui-react's `carrierData.js`).
 */

/** A promoted carrier: its name is a proper noun, so it is never translated. */
interface CarrierLink {
    name: string
    path: string
}

/** The "all companies" entry is appended by the builder — it is copy, not a carrier. */
const CARRIERS: Record<Locale, CarrierLink[]> = {
    'en-us': [
        { name: 'Amtrak', path: '/carrier/amtrak-train-146/' },
        { name: 'Acela', path: '/carrier/acela-182/' },
        { name: 'Greyhound Bus', path: '/carrier/greyhound-bus-lines-139/' },
        { name: 'FlixBus USA', path: '/carrier/flixbus-us-1956/' },
        { name: 'Peter Pan Bus', path: '/carrier/peter-pan-bus-lines-132/' },
        { name: 'Red Coach', path: '/carrier/red-coach-148/' },
        { name: 'California Zephyr', path: '/carrier/amtrak-california-zephyr-186/' },
    ],
    'es-mx': [
        { name: 'Tornado Bus', path: '/carrier/tornado-bus-company-74/' },
        { name: 'Tufesa International', path: '/carrier/tufesa-international-123/' },
        { name: 'El Expresso', path: '/carrier/el-expreso-bus-235/' },
        { name: 'AVE Senda', path: '/carrier/ave-senda-169/' },
        { name: 'Transportes del Norte', path: '/carrier/transportes-del-norte-tdn-164/' },
        { name: 'Greyhound Bus', path: '/carrier/greyhound-bus-lines-139/' },
        { name: 'Autobuses Torres', path: '/carrier/autobuses-torres-1266/' },
    ],
    'en-ca': [
        { name: 'VIA Rail', path: '/carrier/via-rail-133/' },
        { name: 'Megabus', path: '/carrier/megabus-136/' },
        { name: 'Rider Express', path: '/carrier/rider-express-1333/' },
        { name: 'Ontario Northland', path: '/carrier/ontario-northland-1189/' },
        { name: 'Maritime Bus', path: '/carrier/maritime-bus-241/' },
        { name: 'Red Arrow', path: '/carrier/red-arrow-98/' },
        { name: 'Ebus', path: '/carrier/ebus-38/' },
    ],
    // VIA Rail links its own page rather than the `/carrier/` index: the closing
    // "all companies" entry already links the index.
    'fr-ca': [
        { name: 'VIA Rail', path: '/carrier/via-rail-133/' },
        { name: 'Ebus', path: '/carrier/ebus-38/' },
        { name: 'Red Arrow', path: '/carrier/red-arrow-98/' },
        { name: 'Maritime Bus', path: '/carrier/maritime-bus-241/' },
        { name: 'Cold Shot', path: '/carrier/cold-shot-bus-1338/' },
        { name: 'Ontario Northland', path: '/carrier/ontario-northland-1189/' },
        { name: 'Greyhound Bus', path: '/carrier/greyhound-bus-lines-139/' },
    ],
    'it-it': [
        { name: 'FlixBus', path: '/carrier/flixbus-206/' },
        { name: 'Sita', path: '/carrier/sita-1085/' },
        { name: 'Marozzi', path: '/carrier/marozzi-1083/' },
        { name: 'Itabus', path: '/carrier/itabus-1959/' },
        { name: 'Cortina Express', path: '/carrier/cortina-express-1927/' },
        { name: 'Marino Bus', path: '/carrier/marino-1081/' },
        { name: 'BlaBlaBus', path: '/carrier/blablabus-1955/' },
    ],
    'nl-nl': [
        { name: 'FlixBus', path: '/carrier/flixbus-206/' },
        { name: 'Deutsche Bahn', path: '/carrier/deutsche-bahn-db-196/' },
        { name: 'National Express', path: '/carrier/national-express-coach-224/' },
        { name: 'RailJet', path: '/carrier/railjet-238/' },
        { name: 'Amsterdam Airport Express', path: '/carrier/amsterdam-airport-express-1196/' },
        { name: 'CityBusExpress', path: '/carrier/citybusexpress-243/' },
        { name: 'BlaBlaBus', path: '/carrier/blablabus-1955/' },
    ],
    'de-de': [
        { name: 'FlixBus', path: '/carrier/flixbus-206/' },
        { name: 'Deutsche Bahn', path: '/carrier/deutsche-bahn-db-196/' },
        { name: 'National Express', path: '/carrier/national-express-coach-224/' },
        { name: 'DB ICE - Intercity Express', path: '/carrier/db-intercity-express-1015/' },
        { name: 'DB Intercity - IC', path: '/carrier/deutsche-bahn-intercity-db-ic-208/' },
        { name: 'BlaBlaBus', path: '/carrier/blablabus-1955/' },
        { name: 'Amtrak', path: '/carrier/amtrak-train-146/' },
    ],
    'en-gb': [
        { name: 'FlixBus', path: '/carrier/flixbus-206/' },
        { name: 'Deutsche Bahn', path: '/carrier/deutsche-bahn-db-196/' },
        { name: 'National Express', path: '/carrier/national-express-coach-224/' },
        { name: 'Eurostar', path: '/carrier/eurostar-1952/' },
        { name: 'BlaBlaBus', path: '/carrier/blablabus-1955/' },
        { name: 'Greyhound Bus', path: '/carrier/greyhound-bus-lines-139/' },
        { name: 'Amtrak', path: '/carrier/amtrak-train-146/' },
    ],
    // The ferry line carries its name with the accent; legacy's header drops it.
    'es-es': [
        { name: 'FlixBus', path: '/carrier/flixbus-206/' },
        { name: 'Deutsche Bahn', path: '/carrier/deutsche-bahn-db-196/' },
        { name: 'Baleària', path: '/carrier/balearia-1226/' },
        { name: 'BlaBlaBus', path: '/carrier/blablabus-1955/' },
        { name: 'Greyhound Bus', path: '/carrier/greyhound-bus-lines-139/' },
        { name: 'Amtrak', path: '/carrier/amtrak-train-146/' },
    ],
    'fr-fr': [
        { name: 'FlixBus', path: '/carrier/flixbus-206/' },
        { name: 'Deutsche Bahn', path: '/carrier/deutsche-bahn-db-196/' },
        { name: 'National Express', path: '/carrier/national-express-coach-224/' },
        { name: 'Navette Paris-Beauvais', path: '/carrier/navette-paris-beauvais-1184/' },
        { name: 'Le Bus Direct', path: '/carrier/le-bus-direct-1154/' },
        { name: 'Eurostar', path: '/carrier/eurostar-1952/' },
        { name: 'BlaBlaBus', path: '/carrier/blablabus-1955/' },
    ],
}

const CARRIER_INDEX_PATH = '/carrier/'

/**
 * The blog is a single shared surface — it carries no locale prefix. Trailing slash
 * required: `trailingSlash: true` (next.config) 308s `/blog`, and this is both the
 * menu's Blog entry and the blog masthead's brand link on every blog page.
 */
export const blogUrl = '/blog/'

/**
 * Prefix a site path with the active locale: `/carrier/` → `/es-mx/carrier/`.
 *
 * Which entries are also marked `native` follows who serves them. Nexus serves none of
 * the ticket, carrier, explore or account paths this header promotes — the edge routes
 * them to the legacy application — so each renders as a plain anchor rather than a routed
 * link that would prefetch a page this app cannot render. The c2c routes and the blog
 * are this app's and stay routed.
 */
function localePath(locale: Locale, path: string): string {
    return `/${locale}${path}`
}

/**
 * A locale's own homepage. The default locale's is the bare `/` — `/en-us/` 301s to
 * it (`src/proxy.ts`) — so this is the only shape that does not redirect. Shared with
 * the country picker (`locale-menu-config.ts`), which needs the same answer.
 *
 * Takes a `PlatformLocale`, not the narrower `Locale`: the picker asks it for the home
 * of every market the platform serves, including the ones only legacy renders. The
 * logo's own callers still pass a `Locale`, which is a subset.
 */
export function localeHomeUrl(locale: PlatformLocale): string {
    return locale === defaultLocale ? '/' : `/${locale}/`
}

/** The logo target: the locale's own home — for en-us, the bare root. */
export function buildSiteLogo(locale: Locale): NavbarLogo {
    return {
        url: localeHomeUrl(locale),
        src: '/next-static/logos/chiku-and-wanderu-horizontal.svg',
        width: 192,
        height: 44,
        alt: 'logo',
    }
}

/** A city as a route anchor names it: its short form where the locale writes one (NYC), else
 *  its name. */
export function menuCityLabel(city: CityCopy | undefined): string | undefined {
    return (city?.shortName ?? city?.name)?.text
}

/** A city's copy in the menu's locale, by wcityid. */
export type MenuCityCopy = (id: string) => CityCopy | undefined

/** A mode's promoted routes, each anchor naming its two cities; a route a city of which has
 *  no name in the locale is left out rather than drawn with an empty name. */
function routeItems(locale: Locale, mode: MenuMode, copy: MenuCopy, cities: MenuCityCopy) {
    const anchor = mode === 'bus' ? copy.busRoute : copy.trainRoute
    return menuRoutes(locale, mode).flatMap((route) => {
        const from = menuCityLabel(cities(route.from))
        const to = menuCityLabel(cities(route.to))
        return from === undefined || to === undefined
            ? []
            : [{ title: anchor({ from, to }).text, url: localePath(locale, route.path) }]
    })
}

export function buildSiteMenu(locale: Locale, copy: MenuCopy, cities: MenuCityCopy): MenuItem[] {
    return [
        {
            title: copy.buses.text,
            url: localePath(locale, '/bus-tickets/'),
            native: true,
            items: routeItems(locale, 'bus', copy, cities),
        },
        {
            title: copy.trains.text,
            url: localePath(locale, '/train-tickets/'),
            native: true,
            items: routeItems(locale, 'train', copy, cities),
        },
        {
            title: copy.carriers.text,
            shortTitle: copy.carriersShort.text,
            url: localePath(locale, CARRIER_INDEX_PATH),
            native: true,
            items: [
                ...CARRIERS[locale].map((carrier) => ({
                    title: carrier.name,
                    url: localePath(locale, carrier.path),
                    native: true,
                })),
                {
                    title: copy.allCarriers.text,
                    url: localePath(locale, CARRIER_INDEX_PATH),
                    native: true,
                },
            ],
        },
        // Load-bearing: this must stay `/<locale>/explore/`, not the `/find-tickets/` it
        // sometimes redirects to. Explore is a live experiment route — the router serves
        // control in place and 302s only the treatment bucket at the target — so linking
        // the target forces every visitor into the treatment page and contaminates the
        // experiment. The 302 is a per-visitor bucket, not a permanent edge redirect.
        //
        // `native` is load-bearing for the same reason: a routed link prefetches this
        // address on viewport entry, reaching the bucket assignment with no page view
        // behind it — from the header of every page.
        { title: copy.explore.text, url: localePath(locale, '/explore/'), native: true },
        { title: copy.blog.text, url: blogUrl },
    ]
}

export function buildSiteAuth(locale: Locale, copy: SiteAuthCopy): NavbarAuth {
    return {
        login: { title: copy.login.text, url: localePath(locale, '/account/login/'), native: true },
        signup: {
            title: copy.signup.text,
            url: localePath(locale, '/account/signup/'),
            native: true,
        },
        myTrips: {
            title: copy.myTrips.text,
            url: localePath(locale, '/account/my-trips/'),
            native: true,
        },
        account: {
            title: copy.account.text,
            url: localePath(locale, '/account/settings/'),
            native: true,
        },
        points: {
            title: copy.points.text,
            url: localePath(locale, '/account/points/'),
            native: true,
        },
    }
}
